<template>
  <AppDialog
    :model-value="modelValue"
    :persistent="false"
    title="Choose Profile Photo"
    max-width="900px"
    @update:model-value="onDialogChange"
    @close="cancel"
  >
    <v-card-text class="pt-4">
      <v-progress-linear
        indeterminate
        :active="loadingImages"
        color="primary"
      />
      <div class="picker-grid-container">
        <v-row class="mt-2">
          <v-col
            v-for="image in mergedImages"
            :key="image.id"
            cols="3"
          >
            <v-card
              variant="outlined"
              :class="{ 'picker-selected': selectedImageId === image.id }"
              class="picker-card"
              @click="selectedImageId = image.id"
            >
              <v-img
                :src="resolveStorageUrl(image.thumbnailUrl)"
                aspect-ratio="1"
                class="bg-grey-lighten-2"
                cover
              />
              <v-card-subtitle class="py-2 picker-subtitle">
                <div class="text-truncate">
                  {{ displayLabel(image) }}
                </div>
                <v-chip
                  size="x-small"
                  :color="chipColor(image)"
                  class="mt-1"
                >
                  {{ chipText(image) }}
                </v-chip>
              </v-card-subtitle>
            </v-card>
          </v-col>
          <v-col
            v-if="!loadingImages && mergedImages.length === 0"
            cols="12"
          >
            <span class="text-caption">No photos available to choose from.</span>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="cancel">
        <v-icon>mdi-close</v-icon>
        Cancel
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="selectedImageId == null"
        @click="confirm"
      >
        <v-icon>mdi-check</v-icon>
        Use Selected Photo
      </v-btn>
    </v-card-actions>
  </AppDialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppDialog from "@/components/AppDialog";
import { imageDisplayLabel } from "@/helpers/imageDisplay";

export default {
  components: { AppDialog },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    whaleId: {
      type: String,
      default: "",
    },
    currentImageId: {
      type: Number,
      default: null,
    },
  },
  emits: ["update:modelValue", "pick"],
  data() {
    return {
      selectedImageId: null,
    };
  },
  computed: {
    ...mapGetters({
      whaleImages: "whale/details/whaleImages",
      unassociatedImages: "whale/details/unassociatedImages",
      loadingImages: "whale/details/loadingImages",
    }),
    mergedImages() {
      const seen = new Set();
      const result = [];
      const list = (this.whaleImages || []).concat(this.unassociatedImages || []);
      for (const img of list) {
        if (!seen.has(img.id)) {
          seen.add(img.id);
          result.push(img);
        }
      }
      return result;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.selectedImageId = this.currentImageId;
        this.loadPickerImages(this.whaleId);
      }
    },
  },
  methods: {
    ...mapActions({
      loadPickerImages: "whale/details/loadPickerImages",
    }),
    resolveStorageUrl(url) {
      if (!url) return "";
      if (url.startsWith("http")) return url;
      return `${process.env.VUE_APP_API_URL}${url}`;
    },
    isAssociated(image) {
      return !!image.whaleId && image.whaleId !== '' && image.whaleId !== 'na';
    },
    chipText(image) {
      return this.isAssociated(image) ? image.whaleId : 'Unassociated';
    },
    chipColor(image) {
      return (this.isAssociated(image) && image.whaleId === this.whaleId) ? 'primary' : undefined;
    },
    displayLabel(image) {
      return imageDisplayLabel(image);
    },
    onDialogChange(value) {
      if (!value) {
        this.cancel();
      }
    },
    cancel() {
      this.$emit("update:modelValue", false);
    },
    confirm() {
      const picked = this.mergedImages.find(
        (img) => img.id === this.selectedImageId
      );
      if (picked) {
        this.$emit("pick", picked);
      }
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.picker-grid-container {
  max-height: 480px;
  overflow-y: auto;
}
.picker-card {
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.picker-selected {
  border-color: #1976d2 !important;
  border-width: 2px !important;
}
.picker-subtitle {
  font-size: 0.75rem;
}
</style>
